import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Eye } from "lucide-react";
import { API, api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Documents() {
  const { user } = useAuth();
  const isAssoc = user?.role === "associate";
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState([]);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    const params = category === "all" ? {} : { category };
    api.get("/documents", { params }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  }, [category]);

  useEffect(() => { api.get("/cases").then(({ data }) => setCases(data)).catch(() => {}); }, []);
  const caseMap = Object.fromEntries(cases.map((c) => [c.id, c]));

  return (
    <div data-testid="documents-page">
      <PageHeader
        eyebrow="Record"
        title="Pleadings & Documents"
        description="Pleadings, evidence, orders and awards across all your references. Upload from within a case file."
        actions={
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-none w-52" data-testid="documents-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {["pleading", "application", "evidence", "order", "interim_order", "award", "correspondence"].map((c) => (
                <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      {rows.length === 0 ? (
        <EmptyState testId="documents-empty" title="No documents on record" hint="Open a case file and upload the first pleading." />
      ) : (
        <div className="border border-border bg-card overflow-x-auto" data-testid="documents-table">
          <table className="w-full text-sm min-w-[780px]">
            <thead>
              <tr className="border-b border-border bg-muted/60">
                {["Title", "Case", "Category", "File", "Size", "Uploaded", ""].map((h) => (
                  <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-b border-border last:border-0 hover:bg-accent transition-colors duration-200" data-testid={`document-list-row-${d.id}`}>
                  <td className="p-4 font-medium">{d.title}</td>
                  <td className="p-4">
                    <Link to={`/cases/${d.case_id}`} className="font-mono text-xs text-blue-900 dark:text-blue-400 hover:underline">
                      {caseMap[d.case_id]?.case_number || caseMap[d.case_id]?.ref_id || "—"}
                    </Link>
                  </td>
                  <td className="p-4"><Badge className="bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">{d.category}</Badge></td>
                  <td className="p-4 text-muted-foreground font-mono text-xs truncate max-w-[220px]">{d.filename}</td>
                  <td className="p-4 font-mono text-xs">{Math.round((d.size || 0) / 1024)} KB</td>
                  <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDate(d.created_at)}</td>
                  <td className="p-4">
                    {isAssoc ? (
                      <a href={`${API}/documents/${d.id}/view`} target="_blank" rel="noreferrer"
                        data-testid={`document-list-view-${d.id}`}
                        className="text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                        <Eye className="h-4 w-4" />
                      </a>
                    ) : (
                    <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                      data-testid={`document-list-download-${d.id}`}
                      className="text-blue-900 dark:text-blue-400 hover:opacity-70 transition-opacity duration-200">
                      <Download className="h-4 w-4" />
                    </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
